import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Logo } from "@/components/brand";
import { HandoverCard } from "@/components/credential-card";
import {
  MAX_LENGTH,
  MIN_LENGTH,
  PasswordField,
  ReasonNotice,
  Requirements,
  passwordRules,
  passwordValid,
  UsernameForManager,
} from "@/components/password-fields";
import { Showcase } from "@/components/showcase";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { formatWait } from "@/lib/utils";
import type { TokenPair } from "@/lib/types";
import { useAuthStore } from "@/stores/auth";
import { useTranslation } from "@/stores/locale";

/**
 * Where sign-in sends an account that must change its password before it
 * gets a session. Sign-in left a five-minute token in session storage; this
 * screen spends it, and the answer is the token pair sign-in withheld.
 */
export const PASSWORD_CHANGE_TOKEN_KEY = "password_change_token";

/**
 * When that token dies, as milliseconds since the epoch.
 *
 * Sign-in writes it from the `expires_in` the API sends beside the token, so
 * the five minutes are the server's number rather than one copied into the
 * frontend to drift.
 */
export const PASSWORD_CHANGE_DEADLINE_KEY = "password_change_deadline";

/**
 * Who the handoff is for. The token knows, but it is opaque to us, and a
 * password manager needs the name to attribute what it is about to generate.
 */
export const PASSWORD_CHANGE_USER_KEY = "password_change_user";

/** Forget the handoff — it has been spent, or it has run out. */
export function clearPasswordChange() {
  sessionStorage.removeItem(PASSWORD_CHANGE_TOKEN_KEY);
  sessionStorage.removeItem(PASSWORD_CHANGE_DEADLINE_KEY);
  sessionStorage.removeItem(PASSWORD_CHANGE_USER_KEY);
}

export const Route = createFileRoute("/_auth/set-password")({
  beforeLoad: () => {
    // Nothing to spend: the person did not come from sign-in, reloaded late, or
    // left the tab sitting here past the five minutes.
    const deadline = Number(sessionStorage.getItem(PASSWORD_CHANGE_DEADLINE_KEY));
    if (!sessionStorage.getItem(PASSWORD_CHANGE_TOKEN_KEY) || !(deadline > Date.now())) {
      clearPasswordChange();
      throw redirect({ to: "/login" });
    }
  },
  component: SetPasswordPage,
});

const schema = z
  .object({
    new_password: z.string().min(MIN_LENGTH).max(MAX_LENGTH).refine(passwordValid),
    confirm_password: z.string().min(1),
  })
  .refine((values) => values.new_password === values.confirm_password, {
    path: ["confirm_password"],
  });

type SetInput = z.infer<typeof schema>;

function SetPasswordPage() {
  const t = useTranslation();
  const navigate = useNavigate();
  const signIn = useAuthStore((state) => state.signIn);

  const form = useForm<SetInput>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { new_password: "", confirm_password: "" },
  });
  const [next, confirm] = useWatch({
    control: form.control,
    name: ["new_password", "confirm_password"],
  });

  const rules = [
    ...passwordRules(next),
    { key: "pw.ruleMatch" as const, met: next.length > 0 && next === confirm },
  ];
  const ready = rules.every((rule) => rule.met);

  // Read once: the deadline was fixed when sign-in wrote it, and re-reading it
  // every render would only invite a torn value mid-navigation.
  const [deadline] = useState(() =>
    Number(sessionStorage.getItem(PASSWORD_CHANGE_DEADLINE_KEY)),
  );
  const [now, setNow] = useState(() => Date.now());
  const remaining = Math.max(0, Math.ceil((deadline - now) / 1000));

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const mutation = useMutation({
    mutationFn: async (new_password: string) =>
      (
        await api.post<TokenPair>("/auth/password-change", {
          token: sessionStorage.getItem(PASSWORD_CHANGE_TOKEN_KEY),
          new_password,
        })
      ).data,
    onSuccess: (tokens) => {
      clearPasswordChange();
      signIn(tokens);
      toast.success(t("pw.success"));
      void navigate({ to: "/" });
    },
    onError: (error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      // The token lived five minutes; past that the only way on is sign-in.
      if (status === 401) {
        clearPasswordChange();
        toast.error(t("pw.tokenExpired"));
        void navigate({ to: "/login" });
        return;
      }
      toast.error(errorMessage(error, t("pw.failed")));
    },
  });

  // The token is dead, so the form on screen can no longer be spent: say so
  // once and send them back for a fresh one.
  useEffect(() => {
    if (remaining > 0) return;
    clearPasswordChange();
    toast.error(t("pw.tokenExpired"));
    void navigate({ to: "/login" });
  }, [remaining, navigate, t]);

  return (
    <div className="grid flex-1 gap-8 px-5 pt-6 pb-24 sm:px-8 lg:grid-cols-[minmax(0,1fr)_min(571px,42%)] lg:items-stretch lg:gap-10 lg:px-8 lg:py-8">
      <main className="flex flex-col">
        <div className="flex flex-1 items-center py-10 lg:py-6">
          <div className="mx-auto w-full max-w-[32.5rem]">
            <Logo className="mb-10 w-[140px]" />

            <h1 className="text-title-1 text-heading">{t("pw.title")}</h1>
            <p className="text-fg-muted text-body mt-2 text-pretty">{t("pw.subtitle")}</p>

            <form
              className="mt-8 grid gap-4"
              onSubmit={form.handleSubmit((values) => mutation.mutate(values.new_password))}
              noValidate
            >
              {/* Sign-in only sends someone here for one reason: the password
                  they just used was issued to them. The countdown is outside
                  the live region — a number that changes every second would be
                  read out every second. */}
              <ReasonNotice>
                {t("pw.forced")}
                <span aria-live="off" className="text-fg-muted mt-0.5 block">
                  {t("pw.expiresIn").replace("{time}", formatWait(remaining))}
                </span>
              </ReasonNotice>

              <UsernameForManager
                value={sessionStorage.getItem(PASSWORD_CHANGE_USER_KEY) ?? ""}
              />

              <PasswordField
                id="new_password"
                label={t("pw.new")}
                autoComplete="new-password"
                autoFocus
                register={form.register("new_password")}
              />
              <PasswordField
                id="confirm_password"
                label={t("pw.confirm")}
                autoComplete="new-password"
                register={form.register("confirm_password")}
              />

              <Requirements rules={rules} />

              <Button
                type="submit"
                size="xl"
                className="mt-2 w-full"
                disabled={!ready}
                loading={mutation.isPending}
                loadingLabel={t("pw.saving")}
              >
                {t("pw.submit")}
              </Button>
            </form>
          </div>
        </div>
      </main>

      {/* Decoration earns its place beside the form, not stacked under it on a
          phone, where it only pushes the footer out of reach. */}
      <div className="hidden lg:block">
        <Showcase
          visual={<HandoverCard />}
          title={t("showcase.pwFirstTitle")}
          subtitle={t("showcase.pwFirstBody")}
        />
      </div>
    </div>
  );
}
